import { useEffect, useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode, type RefObject } from "react";

interface MerrySidePopupProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
  className?: string;
  label: string;
  open: boolean;
  width: "compact" | "wide";
  id?: string;
  anchorRef?: RefObject<HTMLElement | null>;
  anchorAlignment?: "bottom" | "top";
}

/** Shared glass shell for the left-side workspace popups. */
export function MerrySidePopup({ children, className = "", label, open, width, id, anchorRef, anchorAlignment, ...rest }: MerrySidePopupProps) {
  const elementRef = useRef<HTMLElement>(null);
  const previousOpen = useRef(open);
  const [retainedChildren, setRetainedChildren] = useState(children);
  if (open && children !== retainedChildren) setRetainedChildren(children);

  useEffect(() => {
    if (open) return;
    const timeout = window.setTimeout(() => setRetainedChildren(null), 220);
    return () => window.clearTimeout(timeout);
  }, [open]);

  useLayoutEffect(() => {
    if (!open && !previousOpen.current) return;
    previousOpen.current = open;
    const popup = elementRef.current;
    if (!popup) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animation = popup.animate(
      open
        ? [{ opacity: 0, transform: "translateX(0)" }, { opacity: 1, transform: "translateX(0)" }]
        : [{ opacity: 1, transform: "translateX(0)" }, { opacity: 0, transform: "translateX(-12px)" }],
      { duration: reducedMotion ? 0 : 220, easing: "ease" },
    );
    return () => animation.cancel();
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !anchorRef || !anchorAlignment) return;
    const popup = elementRef.current;
    const anchor = anchorRef.current;
    if (!popup || !anchor) return;

    const updatePosition = () => {
      const viewportHeight = window.innerHeight;
      const edge = Math.min(24, viewportHeight / 2);
      const popupHeight = popup.getBoundingClientRect().height;
      const anchorBottom = anchor.getBoundingClientRect().bottom;
      const preferredTop = anchorAlignment === "bottom" ? anchorBottom - popupHeight : anchorBottom;
      const top = Math.max(edge, Math.min(preferredTop, viewportHeight - edge - popupHeight));
      popup.style.top = `${top}px`;
    };

    updatePosition();
    const observer = new ResizeObserver(updatePosition);
    observer.observe(popup);
    observer.observe(anchor);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);

    };
  }, [open, anchorRef, anchorAlignment]);

  return <aside aria-label={label} aria-hidden={!open || undefined} className={`merry-side-popup merry-side-popup--${width} ${className}`} data-open={open} id={id} ref={elementRef} inert={!open || undefined} {...rest}>
    {open ? children : retainedChildren}
  </aside>;
}
